<template>
  <div v-if="showInstallPrompt && !isInstalled()" class="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-gray-200 shadow-lg">
    <div class="max-w-md mx-auto flex items-center justify-between gap-4">
      <div class="flex-1">
        <p class="text-sm font-medium text-gray-900">Install LeadHub</p>
        <p class="text-xs text-gray-600">Add to your home screen for a better experience</p>
      </div>
      <div class="flex gap-2">
        <button
          @click="dismissPrompt"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Later
        </button>
        <button
          @click="installPWA"
          class="px-4 py-2 text-sm font-medium text-white bg-[#F07B00] rounded-lg hover:bg-[#d96a00] transition-colors"
        >
          Install
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PWAInstallPrompt',
  data() {
    return {
      showInstallPrompt: false,
      deferredPrompt: null,
      installCheckInterval: null
    }
  },
  mounted() {
    // Check if already installed
    if (this.isInstalled()) {
      return
    }

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-install-dismissed')
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10)
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24)
      // Show again after 7 days
      if (daysSinceDismissed < 7) {
        return
      }
    }

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt)
    
    // Listen for app installed event
    window.addEventListener('appinstalled', this.handleAppInstalled)
    
    // Periodically check if app is installed (in case user installed it manually)
    this.installCheckInterval = setInterval(() => {
      if (this.isInstalled()) {
        this.hidePrompt()
      }
    }, 1000) // Check every second
    
    // Show prompt after a delay if not already shown
    setTimeout(() => {
      if (this.deferredPrompt && !this.isInstalled()) {
        this.showInstallPrompt = true
      }
    }, 3000) // Show after 3 seconds
  },
  beforeUnmount() {
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', this.handleAppInstalled)
    if (this.installCheckInterval) {
      clearInterval(this.installCheckInterval)
    }
  },
  methods: {
    handleBeforeInstallPrompt(e) {
      // Don't show if already installed
      if (this.isInstalled()) {
        return
      }
      
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault()
      // Stash the event so it can be triggered later
      this.deferredPrompt = e
      
      // Only show if not already installed
      if (!this.isInstalled()) {
        this.showInstallPrompt = true
      }
    },
    handleAppInstalled() {
      // App was installed, hide the prompt
      console.log('PWA was installed')
      this.hidePrompt()
      // Clear any stored dismissal since app is now installed
      localStorage.removeItem('pwa-install-dismissed')
    },
    hidePrompt() {
      this.showInstallPrompt = false
      this.deferredPrompt = null
      if (this.installCheckInterval) {
        clearInterval(this.installCheckInterval)
        this.installCheckInterval = null
      }
    },
    async installPWA() {
      // Double check if already installed
      if (this.isInstalled()) {
        this.hidePrompt()
        return
      }
      
      if (!this.deferredPrompt) {
        // Fallback for iOS or browsers that don't support the install prompt
        this.showIOSInstructions()
        this.dismissPrompt()
        return
      }

      // Show the install prompt
      this.deferredPrompt.prompt()

      // Wait for the user to respond to the prompt
      const { outcome } = await this.deferredPrompt.userChoice

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt')
        // Hide prompt immediately after acceptance
        this.hidePrompt()
      } else {
        console.log('User dismissed the install prompt')
        // Keep prompt visible if dismissed (user might want to install later)
        this.deferredPrompt = null
      }
    },
    dismissPrompt() {
      this.showInstallPrompt = false
      // Remember dismissal for 7 days
      localStorage.setItem('pwa-install-dismissed', Date.now().toString())
    },
    isInstalled() {
      // Check if running as standalone (installed PWA)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        return true
      }
      
      // Check iOS standalone mode
      if (window.navigator.standalone === true) {
        return true
      }
      
      // Check if launched from Android app (android-app:// referrer)
      if (document.referrer.includes('android-app://')) {
        return true
      }
      
      // Check if running in fullscreen mode (another PWA indicator)
      if (window.matchMedia('(display-mode: fullscreen)').matches) {
        return true
      }
      
      return false
    },
    showIOSInstructions() {
      // Show instructions for iOS users
      this.$swal.fire({
        title: 'Install LeadHub',
        html: `
          <p class="text-left mb-4">To install this app on your iOS device:</p>
          <ol class="text-left list-decimal list-inside space-y-2">
            <li>Tap the Share button <span style="display: inline-block; width: 20px; height: 20px; background: #F07B00; border-radius: 4px; vertical-align: middle; margin: 0 4px;"></span> at the bottom of the screen</li>
            <li>Scroll down and tap "Add to Home Screen"</li>
            <li>Tap "Add" in the top right corner</li>
          </ol>
        `,
        icon: 'info',
        confirmButtonColor: '#F07B00'
      })
    }
  }
}
</script>

